import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import {
  LocalPost,
  PostTopicType,
  gbpPostsApi,
} from "../../../services/gbp";
import { profilesTrackingService } from "../../../services";
import { Profile } from "../types";
import {
  buildPostDraftSuggestions,
  gabaritoImageNames,
  gabaritoKeywords,
  type PostDraftSuggestion,
} from "../profile-content-suggestions";
import { FieldSyncBadge } from "./FieldSyncBadge";
import {
  readGabaritoState,
  renameFileWithRandomKeyword,
} from "./gabarito-utils";

// ==================== Helpers ====================

const formatDate = (dateString?: string) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTypeIcon = (type: PostTopicType) => {
  switch (type) {
    case "EVENT":
      return "event";
    case "OFFER":
      return "local_offer";
    case "ALERT":
      return "warning";
    default:
      return "article";
  }
};

const getTypeLabel = (type: PostTopicType) => {
  switch (type) {
    case "EVENT":
      return "Evento";
    case "OFFER":
      return "Oferta";
    case "ALERT":
      return "Alerta";
    default:
      return "Publicação";
  }
};

const getCTALabel = (actionType?: string) => {
  switch (actionType) {
    case "BOOK":
      return "Reservar";
    case "ORDER":
      return "Pedir";
    case "SHOP":
      return "Comprar";
    case "LEARN_MORE":
      return "Saiba Mais";
    case "SIGN_UP":
      return "Inscrever-se";
    case "CALL":
      return "Ligar";
    case "GET_OFFER":
      return "Ver Oferta";
    default:
      return "Ver";
  }
};

const getStatusLabel = (status?: string) => {
  switch (status) {
    case "LIVE":
      return "Ativo";
    case "PROCESSING":
      return "Processando";
    case "REJECTED":
      return "Rejeitado";
    default:
      return status || "";
  }
};

const getTypeColor = (type: PostTopicType) => {
  switch (type) {
    case "EVENT":
      return "#3b82f6";
    case "OFFER":
      return "#f59e0b";
    case "ALERT":
      return "#ef4444";
    default:
      return "#6366f1";
  }
};

// ==================== Animations ====================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const slideDown = keyframes`
  from { opacity: 0; max-height: 0; }
  to   { opacity: 1; max-height: 1200px; }
`;

const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${fadeIn} 0.3s ease;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  .material-icons {
    font-size: 22px;
    color: #1264C7;
  }
`;

const PostCount = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  margin-left: 4px;
`;

const AddPostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: #1264C7;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;

  .material-icons { font-size: 18px; }

  &:hover {
    background: #1264C7;
    box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
  }

  &:active { transform: scale(0.97); }
`;

/* ---------- Filtros ---------- */

const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FilterChip = styled.button<{ $active?: boolean }>`
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? "#1264C7" : "#e5e7eb")};
  background: ${({ $active }) => ($active ? "#eff6ff" : "#fff")};
  color: ${({ $active }) => ($active ? "#1d4ed8" : "#6b7280")};
  transition: all 0.15s ease;

  &:hover {
    border-color: #1264C7;
    color: #1d4ed8;
  }
`;

/* ---------- Cards ---------- */

const PostsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PostCard = styled.div<{ $typeColor?: string }>`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 3px solid ${({ $typeColor }) => $typeColor || "#1264C7"};
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.3s ease;

  &:hover {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  }
`;

const PostCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafbfc;
`;

const PostTypeBadge = styled.div<{ $type: PostTopicType }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 12px;
  letter-spacing: 0.3px;

  ${({ $type }) => {
    switch ($type) {
      case "EVENT":
        return `background: #dbeafe; color: #1d4ed8;`;
      case "OFFER":
        return `background: #fef3c7; color: #d97706;`;
      case "ALERT":
        return `background: #fee2e2; color: #dc2626;`;
      default:
        return `background: #e0e7ff; color: #4338ca;`;
    }
  }}

  .material-icons { font-size: 13px; }
`;

const PostStatusBadge = styled.span<{ $status?: string }>`
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  ${({ $status }) => {
    switch ($status) {
      case "LIVE":
        return `background: #d1fae5; color: #059669;`;
      case "PROCESSING":
        return `background: #fef3c7; color: #d97706;`;
      case "REJECTED":
        return `background: #fee2e2; color: #dc2626;`;
      default:
        return `background: #f3f4f6; color: #6b7280;`;
    }
  }}
`;

const PostCardBody = styled.div`
  padding: 14px 16px;
`;

const PostMediaPreview = styled.div`
  width: 100%;
  height: 180px;
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img { transform: scale(1.03); }
`;

const PostSummaryText = styled.p`
  font-size: 14px;
  color: #374151;
  margin: 0;
  line-height: 1.55;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 10px;
  font-size: 12px;
  color: #9ca3af;

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    .material-icons { font-size: 14px; }
  }
`;

const EventBanner = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 14px;
  margin-top: 10px;

  .event-title {
    font-size: 13px;
    font-weight: 600;
    color: #1e40af;
    margin: 0 0 4px;
  }

  .event-time {
    font-size: 12px;
    color: #3b82f6;
    display: flex;
    align-items: center;
    gap: 4px;

    .material-icons { font-size: 14px; }
  }
`;

const OfferBanner = styled.div`
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 10px 14px;
  margin-top: 10px;

  .coupon-code {
    display: inline-block;
    background: #fbbf24;
    color: #78350f;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: 12px;
    margin-bottom: 4px;
  }

  .offer-terms {
    font-size: 12px;
    color: #92400e;
    margin: 4px 0 0;
  }
`;

const CTALink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  font-size: 12px;
  font-weight: 500;
  color: #1264C7;
  text-decoration: none;
  padding: 5px 12px;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  background: #eff6ff;
  transition: all 0.15s ease;

  .material-icons { font-size: 14px; }

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
  }
`;

const PostCardActions = styled.div`
  display: flex;
  gap: 6px;
  padding: 10px 16px;
  border-top: 1px solid #f3f4f6;
  background: #fafbfc;
`;

const ActionBtn = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  ${({ $danger }) =>
    $danger
      ? css`
          background: #fef2f2;
          color: #dc2626;
          &:hover { background: #fee2e2; }
        `
      : css`
          background: #f3f4f6;
          color: #6b7280;
          &:hover { background: #e5e7eb; color: #374151; }
        `}

  .material-icons { font-size: 15px; }
`;

/* ---------- Formulário inline ---------- */

const FormOverlay = styled.div`
  animation: ${slideDown} 0.35s ease;
  overflow: hidden;
`;

const AddPostForm = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
`;

const FormTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  .material-icons {
    font-size: 20px;
    color: #1264C7;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #1264C7;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }

  &::placeholder { color: #94a3b8; }
`;

const TextArea = styled.textarea`
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  background: white;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #1264C7;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }

  &::placeholder { color: #94a3b8; }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #1264C7;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }
`;

const TypeSelector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TypeOption = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 2px solid ${({ $active }) => ($active ? "#1264C7" : "#e5e7eb")};
  background: ${({ $active }) => ($active ? "#eff6ff" : "white")};
  color: ${({ $active }) => ($active ? "#1264C7" : "#6b7280")};

  &:hover { border-color: #1264C7; }
  .material-icons { font-size: 16px; }
`;

const ConditionalSection = styled.div`
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ConditionalTitle = styled.h5`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;

  .material-icons {
    font-size: 16px;
    color: #1264C7;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 16px;
`;

const Btn = styled.button<{ $variant?: "primary" | "ghost" | "danger" }>`
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;

  .material-icons { font-size: 17px; }

  ${({ $variant }) =>
    $variant === "primary"
      ? css`
          background: #1264C7;
          color: white;
          border: none;
          &:hover { background: #1264C7; }
          &:disabled { background: #93c5fd; cursor: not-allowed; }
        `
      : $variant === "danger"
      ? css`
          background: #dc2626;
          color: white;
          border: none;
          &:hover { background: #b91c1c; }
          &:disabled { background: #fca5a5; cursor: not-allowed; }
        `
      : css`
          background: transparent;
          color: #64748b;
          border: 1px solid #e2e8f0;
          &:hover { background: #f1f5f9; color: #334155; }
        `}
`;

/* ---------- Estados / Loading / Feedback ---------- */

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: #9ca3af;

  .material-icons {
    font-size: 44px;
    color: #d1d5db;
    margin-bottom: 12px;
  }

  h4 {
    font-size: 15px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 6px;
  }

  p { margin: 0; font-size: 13px; }
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #6b7280;

  .spinner {
    width: 36px;
    height: 36px;
    border: 3px solid #e5e7eb;
    border-top-color: #1264C7;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
  }

  p { margin-top: 14px; font-size: 13px; }
`;

const Toast = styled.div<{ $type: "error" | "success" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  animation: ${fadeIn} 0.25s ease;

  ${({ $type }) =>
    $type === "error"
      ? css`
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
        `
      : css`
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #16a34a;
        `}

  .material-icons { font-size: 18px; }
`;

const DraftPanel = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

const DraftPanelTitle = styled.div`
  color: #0c4a6e;
  font-size: 14px;
  font-weight: 700;
`;

const DraftGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
`;

const DraftCard = styled.div`
  background: #ffffff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
`;

const DraftCardTitle = styled.div`
  color: #1e40af;
  font-size: 13px;
  font-weight: 700;
`;

/* ---------- Modal de Confirmação ---------- */

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 14px;
  width: 100%;
  max-width: 400px;
  animation: ${fadeInScale} 0.25s ease;
  overflow: hidden;
`;

const ModalBoxHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;

  h4 {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
`;

const ModalBoxBody = styled.div`
  padding: 20px;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.5;
`;

const ModalBoxFooter = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 14px 20px;
  border-top: 1px solid #f3f4f6;
  background: #f9fafb;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: #f3f4f6;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { background: #e5e7eb; color: #374151; }
  .material-icons { font-size: 16px; }
`;

// ==================== Component ====================

interface PostsTabProps {
  profile: Profile | null;
  userId?: number;
  /** Cliente com acesso só de leitura: vê as postagens, não cria, edita nem exclui. */
  readOnly?: boolean;
}

export const PostsTab: React.FC<PostsTabProps> = ({ profile, readOnly = false }) => {
  const [posts, setPosts] = useState<LocalPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const publicId = profile?.id;

  // showForm = formulário de NOVA postagem no topo
  // editingPost = formulário de EDIÇÃO inline no card
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<LocalPost | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [postDrafts, setPostDrafts] = useState<PostDraftSuggestion[]>([]);
  const [localFormImageName, setLocalFormImageName] = useState("");
  const [localFormImagePreview, setLocalFormImagePreview] = useState("");

  const [formData, setFormData] = useState<LocalPost>({
    summary: "",
    topicType: "STANDARD",
    languageCode: "pt",
  });

  const gabarito = useMemo(
    () => (profile?.id ? readGabaritoState(profile.id) : null),
    [profile?.id],
  );

  const imageOptions = useMemo(
    () => (gabarito ? gabaritoImageNames(gabarito.images) : []),
    [gabarito],
  );

  // ---------- Derivados ----------

  const uniqueTypes = useMemo(() => {
    const set = new Set<PostTopicType>();
    posts.forEach((p) => set.add(p.topicType));
    return Array.from(set);
  }, [posts]);

  const filteredPosts = useMemo(() => {
    if (activeFilter === "all") return posts;
    return posts.filter((p) => p.topicType === activeFilter);
  }, [posts, activeFilter]);

  const liveCount = useMemo(
    () => posts.filter((p) => p.state === "LIVE").length,
    [posts]
  );

  // ---------- Effects ----------

  const fetchPosts = useCallback(async () => {
    if (!publicId) return;

    try {
      setLoading(true);
      setError(null);

      const postsData = await gbpPostsApi.listPosts(publicId);
      
      // Garante que seja sempre um array
      const postsList = Array.isArray(postsData) ? postsData : [];
      setPosts(postsList);
    } catch (err: unknown) {
      const emsg = err instanceof Error ? err.message : String(err);
      if (emsg.includes("status:501") || /Not Implemented/i.test(emsg)) {
        setError(
          "Postagens via API estão desabilitadas no servidor. Ative 'google.posts.enabled' ou crie posts no painel do Google."
        );
      } else {
        setError("Não foi possível carregar as postagens. Tente novamente mais tarde.");
      }
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    if (!profile || !gabarito) {
      setPostDrafts([]);
      return;
    }

    setPostDrafts(
      buildPostDraftSuggestions({
        profile,
        keywords: gabaritoKeywords(gabarito),
        imageNames: gabaritoImageNames(gabarito.images),
      }),
    );
  }, [gabarito, profile]);

  // ---------- Handlers ----------

  const resetForm = () => {
    setFormData({
      summary: "",
      topicType: "STANDARD",
      languageCode: "pt",
    });
    setLocalFormImageName("");
    setLocalFormImagePreview("");
    setEditingPost(null);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (post: LocalPost) => {
    setShowForm(false); // fecha form de nova postagem se estiver aberto
    setEditingPost(post);
    setFormData({ ...post });
  };

  const updatePostDraft = (
    id: string,
    field: "summary" | "imageKeyword",
    value: string,
  ) => {
    setPostDrafts((drafts) =>
      drafts.map((draft) =>
        draft.id === id ? { ...draft, [field]: value } : draft,
      ),
    );
  };

  const readLocalImage = (file: File, onRead: (src: string) => void) => {
    const reader = new FileReader();
    reader.onload = () => onRead(String(reader.result));
    reader.readAsDataURL(file);
  };

  const handleDraftImageUpload = (id: string, file: File | null) => {
    if (!file) return;

    const draft = postDrafts.find((item) => item.id === id);
    const fallbackKeywords = gabarito ? gabaritoKeywords(gabarito) : [];
    const { file: renamedFile, keyword } = renameFileWithRandomKeyword(file, [
      draft?.imageKeyword || "",
      ...imageOptions,
      ...fallbackKeywords,
      profile?.name || "",
    ]);

    readLocalImage(renamedFile, (src) => {
      setPostDrafts((drafts) =>
        drafts.map((draft) =>
          draft.id === id
            ? {
                ...draft,
                imageKeyword: keyword,
                localImageName: renamedFile.name,
                localImagePreview: src,
              }
            : draft,
        ),
      );
    });
  };

  const handleFormImageUpload = (file: File | null) => {
    if (!file) return;

    const { file: renamedFile } = renameFileWithRandomKeyword(file, [
      ...imageOptions,
      ...(gabarito ? gabaritoKeywords(gabarito) : []),
      profile?.name || "",
    ]);

    readLocalImage(renamedFile, (src) => {
      setLocalFormImageName(renamedFile.name);
      setLocalFormImagePreview(src);
      setFormData((prev) => ({
        ...prev,
        media: [
          {
            mediaFormat: "PHOTO",
            sourceUrl: src,
            description: renamedFile.name,
          },
        ],
      }));
    });
  };

  const applyPostDraft = (draft: PostDraftSuggestion) => {
    setEditingPost(null);
    setFormData({
      summary: draft.summary,
      topicType: draft.topicType,
      languageCode: "pt",
      media: draft.localImagePreview
        ? [
            {
              mediaFormat: "PHOTO",
              sourceUrl: draft.localImagePreview,
              description: draft.localImageName || draft.imageKeyword,
            },
          ]
        : undefined,
    });
    setLocalFormImageName(draft.localImageName || "");
    setLocalFormImagePreview(draft.localImagePreview || "");
    setShowForm(true);
  };

  const closeForm = () => {
    console.log("[PostsTab.closeForm] called");
    setShowForm(false);
    resetForm();
  };

  const closeEditForm = () => {
    setEditingPost(null);
    resetForm();
  };

  // Getters removidos — agora usa publicId direto

  // O Google NÃO aceita imagem em base64 (data:) no post — precisa de URL
  // pública. Sobe cada imagem local pro depósito de fotos de post e troca o
  // sourceUrl pela URL retornada. Imagens que já são URL (ex.: Gabarito) passam
  // direto. Falha no upload não bloqueia o post (mantém como estava).
  const ensurePublicPostMedia = async (
    post: LocalPost,
    profileId: string,
  ): Promise<LocalPost> => {
    if (!post.media?.length) return post;
    const media = await Promise.all(
      post.media.map(async (item) => {
        const url = item.sourceUrl;
        if (typeof url === "string" && url.startsWith("data:")) {
          try {
            const uploaded = await profilesTrackingService.addPostImage(
              profileId,
              { fileName: item.description || "post.jpg", src: url },
            );
            if (uploaded?.url) return { ...item, sourceUrl: uploaded.url };
          } catch {
            // mantém como estava; não bloqueia a publicação
          }
        }
        return item;
      }),
    );
    return { ...post, media };
  };

  const handleSave = async (isEdit = false) => {
    if (!publicId || !formData.summary) return;

    try {
      setSaving(true);
      setError(null);

      const payload = await ensurePublicPostMedia(formData, publicId);

      if (isEdit && editingPost?.postId) {
        const updated = await gbpPostsApi.updatePost(
          publicId,
          editingPost.postId,
          payload
        );
        setPosts(posts.map((p) => (p.postId === editingPost.postId ? updated : p)));
        setSuccess("Postagem atualizada!");
        closeEditForm();
      } else {
        const created = await gbpPostsApi.createPost(publicId, payload);
        setPosts([created, ...posts]);
        setSuccess("Postagem criada!");
        closeForm();
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const emsg = err instanceof Error ? err.message : String(err);
      if (emsg.includes("status:501") || /Not Implemented/i.test(emsg)) {
        setError("Ação não suportada: postagens via API estão desabilitadas no servidor.");
      } else if (emsg.includes("status:502") || /Bad Gateway/i.test(emsg)) {
        // Google retornou 404 ao backend → backend devolveu 502
        const googleDetail = emsg.match(/Google retornou 404[^|]*/i)?.[0];
        setError(
          googleDetail
            ? `Falha na API Google: ${googleDetail.trim()}`
            : "A API do Google para postagens não está disponível. Crie posts diretamente no painel do Google Business Profile."
        );
      } else {
        setError("Erro ao salvar postagem. Tente novamente.");
      }
      setTimeout(() => setError(null), 6000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId: string) => {
    if (!publicId) return;

    try {
      setSaving(true);
      await gbpPostsApi.deletePost(publicId, postId);
      setPosts(posts.filter((p) => p.postId !== postId));
      setDeleteConfirm(null);
      setSuccess("Postagem excluída!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const emsg = err instanceof Error ? err.message : String(err);
      if (emsg.includes("status:501") || /Not Implemented/i.test(emsg)) {
        setError("Ação não suportada: postagens via API estão desabilitadas no servidor.");
      } else if (emsg.includes("status:502") || /Bad Gateway/i.test(emsg)) {
        setError("A API do Google para postagens não está disponível. Exclua posts diretamente no painel do Google Business Profile.");
      } else {
        setError("Erro ao excluir postagem.");
      }
      setTimeout(() => setError(null), 6000);
    } finally {
      setSaving(false);
    }
  };

  // ---------- Renders ----------

  if (loading) {
    return (
      <LoadingOverlay>
        <div className="spinner" />
        <p>Carregando postagens...</p>
      </LoadingOverlay>
    );
  }

  if (!publicId) {
    return (
      <Container>
        <Toast $type="error">
          <span className="material-icons">error_outline</span>
          <span>
            {error || "Perfil não vinculado ao Google Business Profile."}
          </span>
        </Toast>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <HeaderRow>
        <SectionTitle>
          <span className="material-icons">feed</span>
          Postagens
          <FieldSyncBadge kind="backend" />
          <PostCount>
            ({liveCount} ativo{liveCount !== 1 ? "s" : ""} de {posts.length})
          </PostCount>
        </SectionTitle>

        {!readOnly && (
          <AddPostButton
            type="button"
            onClick={() => {
              if (editingPost) { closeEditForm(); return; }
              if (showForm) { closeForm(); } else { openAddForm(); }
            }}
          >
            <span className="material-icons">
              {showForm || editingPost ? "close" : "add"}
            </span>
            {showForm || editingPost ? "Fechar" : "Nova Postagem"}
          </AddPostButton>
        )}
      </HeaderRow>

      {/* Feedback */}
      {error && (
        <Toast $type="error">
          <span className="material-icons">error_outline</span>
          <span>{error}</span>
        </Toast>
      )}
      {success && (
        <Toast $type="success">
          <span className="material-icons">check_circle</span>
          <span>{success}</span>
        </Toast>
      )}

      {!readOnly && postDrafts.length > 0 && (
        <DraftPanel>
          <DraftPanelTitle>
            Sugestões de postagens para validar antes de publicar
            <FieldSyncBadge kind="local" />
          </DraftPanelTitle>
          <DraftGrid>
            {postDrafts.map((draft) => (
              <DraftCard key={draft.id}>
                <DraftCardTitle>{draft.title}</DraftCardTitle>
                <FormGroup>
                  <Label>
                    Foto do Gabarito
                    <FieldSyncBadge kind="local" />
                  </Label>
                  {imageOptions.length > 0 ? (
                    <Select
                      value={draft.imageKeyword}
                      onChange={(event) =>
                        updatePostDraft(
                          draft.id,
                          "imageKeyword",
                          event.target.value,
                        )
                      }
                    >
                      {imageOptions.map((image) => (
                        <option key={image} value={image}>
                          {image}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      value={draft.imageKeyword}
                      onChange={(event) =>
                        updatePostDraft(
                          draft.id,
                          "imageKeyword",
                          event.target.value,
                        )
                      }
                      placeholder="Sem foto cadastrada no Gabarito"
                    />
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>
                    Adicionar foto do computador
                    {/* Era "backend" e enganava: a foto escolhida aqui fica no
                        navegador. Ela so vai para o servidor quando a sugestao
                        vira postagem ("Usar como nova postagem") e o post e
                        salvo — e ai o ensurePublicPostMedia troca o base64 pela
                        URL publica. Quem clicava "Salvar Alteracoes" do perfil
                        via a foto sumir, sem entender por que. */}
                    <FieldSyncBadge kind="local" />
                  </Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      handleDraftImageUpload(
                        draft.id,
                        event.target.files?.[0] || null,
                      );
                      event.target.value = "";
                    }}
                  />
                  {draft.localImageName && (
                    <span style={{ color: "#475569", fontSize: "12px" }}>
                      Foto selecionada: {draft.localImageName} — clique em “Usar
                      como nova postagem” para aplicar; “Salvar Alterações” do
                      perfil não guarda a foto da sugestão.
                    </span>
                  )}
                  {draft.localImagePreview && (
                    <img
                      src={draft.localImagePreview}
                      alt={draft.localImageName || draft.imageKeyword}
                      style={{
                        borderRadius: "8px",
                        maxHeight: "120px",
                        objectFit: "cover",
                        width: "100%",
                      }}
                    />
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>
                    Texto da postagem
                    <FieldSyncBadge kind="backend" />
                  </Label>
                  <TextArea
                    value={draft.summary}
                    onChange={(event) =>
                      updatePostDraft(draft.id, "summary", event.target.value)
                    }
                  />
                </FormGroup>
                <Btn
                  type="button"
                  $variant="primary"
                  onClick={() => applyPostDraft(draft)}
                >
                  <span className="material-icons">edit</span>
                  Usar como nova postagem
                </Btn>
              </DraftCard>
            ))}
          </DraftGrid>
        </DraftPanel>
      )}

      {/* ---------- Formulário de Nova Postagem (topo) ---------- */}
      {showForm && !editingPost && (
        <FormOverlay>
          <AddPostForm>
            <FormTitle>
              <span className="material-icons">post_add</span>
              Nova Postagem
            </FormTitle>

            {/* Tipo */}
            <FormGroup className="full-width" style={{ marginBottom: 14 }}>
              <Label>
                Tipo de Postagem
                <FieldSyncBadge kind="backend" />
              </Label>
              <TypeSelector>
                {(["STANDARD", "EVENT", "OFFER"] as PostTopicType[]).map(
                  (type) => (
                    <TypeOption
                      key={type}
                      type="button"
                      $active={formData.topicType === type}
                      onClick={() =>
                        setFormData({ ...formData, topicType: type })
                      }
                    >
                      <span className="material-icons">
                        {getTypeIcon(type)}
                      </span>
                      {getTypeLabel(type)}
                    </TypeOption>
                  )
                )}
              </TypeSelector>
            </FormGroup>

            {/* Conteúdo */}
            <FormGroup className="full-width" style={{ marginBottom: 14 }}>
              <Label>
                Conteúdo *
                <FieldSyncBadge kind="backend" />
              </Label>
              <TextArea
                value={formData.summary}
                onChange={(e) =>
                  setFormData({ ...formData, summary: e.target.value })
                }
                placeholder="Escreva o conteúdo da sua postagem..."
              />
            </FormGroup>

            {/* Evento */}
            {formData.topicType === "EVENT" && (
              <ConditionalSection style={{ marginBottom: 14 }}>
                <ConditionalTitle>
                  <span className="material-icons">event</span>
                  Detalhes do Evento
                </ConditionalTitle>
                <FormGroup>
                  <Label>Título do Evento</Label>
                  <Input
                    type="text"
                    value={formData.event?.title || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        event: {
                          ...formData.event!,
                          title: e.target.value,
                        },
                      })
                    }
                    placeholder="Nome do evento"
                  />
                </FormGroup>
                <FormGrid>
                  <FormGroup>
                    <Label>Início</Label>
                    <Input
                      type="datetime-local"
                      value={formData.event?.startTime?.slice(0, 16) || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          event: {
                            ...formData.event!,
                            startTime: e.target.value,
                          },
                        })
                      }
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Fim</Label>
                    <Input
                      type="datetime-local"
                      value={formData.event?.endTime?.slice(0, 16) || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          event: {
                            ...formData.event!,
                            endTime: e.target.value,
                          },
                        })
                      }
                    />
                  </FormGroup>
                </FormGrid>
              </ConditionalSection>
            )}

            {/* Oferta */}
            {formData.topicType === "OFFER" && (
              <ConditionalSection style={{ marginBottom: 14 }}>
                <ConditionalTitle>
                  <span className="material-icons">local_offer</span>
                  Detalhes da Oferta
                </ConditionalTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Código do Cupom</Label>
                    <Input
                      type="text"
                      value={formData.offer?.couponCode || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          offer: {
                            ...formData.offer!,
                            couponCode: e.target.value,
                          },
                        })
                      }
                      placeholder="Ex: SAVE20"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>URL de Resgate</Label>
                    <Input
                      type="url"
                      value={formData.offer?.redeemOnlineUrl || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          offer: {
                            ...formData.offer!,
                            redeemOnlineUrl: e.target.value,
                          },
                        })
                      }
                      placeholder="https://..."
                    />
                  </FormGroup>
                </FormGrid>
                <FormGroup>
                  <Label>Termos e Condições</Label>
                  <Input
                    type="text"
                    value={formData.offer?.termsConditions || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        offer: {
                          ...formData.offer!,
                          termsConditions: e.target.value,
                        },
                      })
                    }
                    placeholder="Ex: Válido até 31/12/2026"
                  />
                </FormGroup>
              </ConditionalSection>
            )}

            {/* CTA */}
            <ConditionalSection style={{ marginBottom: 14 }}>
              <ConditionalTitle>
                <span className="material-icons">touch_app</span>
                Botão de Ação (CTA)
              </ConditionalTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Tipo de Ação</Label>
                  <Select
                    value={formData.callToAction?.actionType || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        callToAction: {
                          ...formData.callToAction!,
                          actionType: e.target.value,
                        },
                      })
                    }
                  >
                    <option value="">Sem botão</option>
                    <option value="BOOK">Reservar</option>
                    <option value="ORDER">Pedir</option>
                    <option value="SHOP">Comprar</option>
                    <option value="LEARN_MORE">Saiba Mais</option>
                    <option value="SIGN_UP">Inscrever-se</option>
                    <option value="CALL">Ligar</option>
                    <option value="GET_OFFER">Ver Oferta</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>URL do Botão</Label>
                  <Input
                    type="url"
                    value={formData.callToAction?.url || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        callToAction: {
                          ...formData.callToAction!,
                          url: e.target.value,
                        },
                      })
                    }
                    placeholder="https://..."
                  />
                </FormGroup>
              </FormGrid>
            </ConditionalSection>

            {/* Imagem */}
            <FormGroup className="full-width" style={{ marginBottom: 14 }}>
              <Label>
                URL da Imagem (opcional)
                <FieldSyncBadge kind="backend" />
              </Label>
              <Input
                type="url"
                value={formData.media?.[0]?.sourceUrl || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    media: e.target.value
                      ? [{ mediaFormat: "PHOTO", sourceUrl: e.target.value }]
                      : undefined,
                  })
                }
                placeholder="https://exemplo.com/imagem.jpg"
              />
            </FormGroup>

            <FormGroup className="full-width" style={{ marginBottom: 14 }}>
              <Label>
                Adicionar foto do computador
                <FieldSyncBadge kind="backend" />
              </Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(event) => {
                  handleFormImageUpload(event.target.files?.[0] || null);
                  event.target.value = "";
                }}
              />
              {localFormImageName && (
                <span style={{ color: "#475569", fontSize: "12px" }}>
                  Foto selecionada: {localFormImageName}
                </span>
              )}
              {localFormImagePreview && (
                <img
                  src={localFormImagePreview}
                  alt={localFormImageName}
                  style={{
                    borderRadius: "8px",
                    maxHeight: "180px",
                    objectFit: "cover",
                    width: "100%",
                  }}
                />
              )}
            </FormGroup>

            <FormActions>
              <Btn type="button" $variant="ghost" onClick={closeForm}>
                <span className="material-icons">close</span>
                Cancelar
              </Btn>
              <Btn
                type="button"
                $variant="primary"
                onClick={() => handleSave(false)}
                disabled={saving || !formData.summary}
              >
                <span className="material-icons">send</span>
                {saving ? "Salvando..." : "Publicar"}
              </Btn>
            </FormActions>
          </AddPostForm>
        </FormOverlay>
      )}

      {/* Filtro por tipo */}
      {uniqueTypes.length > 1 && (
        <FilterBar>
          <FilterChip
            type="button"
            $active={activeFilter === "all"}
            onClick={() => setActiveFilter("all")}
          >
            Todos ({posts.length})
          </FilterChip>
          {uniqueTypes.map((type) => (
            <FilterChip
              type="button"
              key={type}
              $active={activeFilter === type}
              onClick={() => setActiveFilter(type)}
            >
              {getTypeLabel(type)} (
              {posts.filter((p) => p.topicType === type).length})
            </FilterChip>
          ))}
        </FilterBar>
      )}

      {/* Lista */}
      {filteredPosts.length === 0 ? (
        <EmptyState>
          <span className="material-icons">article</span>
          <h4>Nenhuma postagem encontrada</h4>
          <p>
            {posts.length === 0
              ? "Crie postagens para engajar seus clientes no Google."
              : "Nenhuma postagem nessa categoria."}
          </p>
        </EmptyState>
      ) : (
        <PostsList>
          {filteredPosts.map((post, index) => (
            <PostCard
              key={post.postId || post.name || `post-${index}`}
              $typeColor={getTypeColor(post.topicType)}
            >
              <PostCardHeader>
                <PostTypeBadge $type={post.topicType}>
                  <span className="material-icons">
                    {getTypeIcon(post.topicType)}
                  </span>
                  {getTypeLabel(post.topicType)}
                </PostTypeBadge>
                {post.state && (
                  <PostStatusBadge $status={post.state}>
                    {getStatusLabel(post.state)}
                  </PostStatusBadge>
                )}
              </PostCardHeader>

              {editingPost?.postId === post.postId ? (
                /* ---------- Formulário de edição inline ---------- */
                <PostCardBody>
                  <AddPostForm style={{ margin: 0, border: 'none', background: '#f8fafc', borderRadius: 0, padding: '16px' }}>
                    <FormTitle>
                      <span className="material-icons">edit_note</span>
                      Editar Postagem
                    </FormTitle>

                    <FormGroup className="full-width" style={{ marginBottom: 14 }}>
                      <Label>
                        Tipo de Postagem
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <TypeSelector>
                        {(["STANDARD", "EVENT", "OFFER"] as PostTopicType[]).map((type) => (
                          <TypeOption
                            key={type}
                            type="button"
                            $active={formData.topicType === type}
                            onClick={() => setFormData({ ...formData, topicType: type })}
                          >
                            <span className="material-icons">{getTypeIcon(type)}</span>
                            {getTypeLabel(type)}
                          </TypeOption>
                        ))}
                      </TypeSelector>
                    </FormGroup>

                    <FormGroup className="full-width" style={{ marginBottom: 14 }}>
                      <Label>
                        Conteúdo *
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <TextArea
                        value={formData.summary}
                        onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                        placeholder="Escreva o conteúdo da sua postagem..."
                      />
                    </FormGroup>

                    {formData.topicType === "EVENT" && (
                      <ConditionalSection style={{ marginBottom: 14 }}>
                        <ConditionalTitle>
                          <span className="material-icons">event</span>
                          Detalhes do Evento
                        </ConditionalTitle>
                        <FormGroup>
                          <Label>Título do Evento</Label>
                          <Input
                            type="text"
                            value={formData.event?.title || ""}
                            onChange={(e) => setFormData({ ...formData, event: { ...formData.event!, title: e.target.value } })}
                            placeholder="Nome do evento"
                          />
                        </FormGroup>
                        <FormGrid>
                          <FormGroup>
                            <Label>Início</Label>
                            <Input type="datetime-local" value={formData.event?.startTime?.slice(0, 16) || ""}
                              onChange={(e) => setFormData({ ...formData, event: { ...formData.event!, startTime: e.target.value } })} />
                          </FormGroup>
                          <FormGroup>
                            <Label>Fim</Label>
                            <Input type="datetime-local" value={formData.event?.endTime?.slice(0, 16) || ""}
                              onChange={(e) => setFormData({ ...formData, event: { ...formData.event!, endTime: e.target.value } })} />
                          </FormGroup>
                        </FormGrid>
                      </ConditionalSection>
                    )}

                    {formData.topicType === "OFFER" && (
                      <ConditionalSection style={{ marginBottom: 14 }}>
                        <ConditionalTitle>
                          <span className="material-icons">local_offer</span>
                          Detalhes da Oferta
                        </ConditionalTitle>
                        <FormGrid>
                          <FormGroup>
                            <Label>Código do Cupom</Label>
                            <Input type="text" value={formData.offer?.couponCode || ""}
                              onChange={(e) => setFormData({ ...formData, offer: { ...formData.offer!, couponCode: e.target.value } })}
                              placeholder="Ex: SAVE20" />
                          </FormGroup>
                          <FormGroup>
                            <Label>URL de Resgate</Label>
                            <Input type="url" value={formData.offer?.redeemOnlineUrl || ""}
                              onChange={(e) => setFormData({ ...formData, offer: { ...formData.offer!, redeemOnlineUrl: e.target.value } })}
                              placeholder="https://..." />
                          </FormGroup>
                        </FormGrid>
                        <FormGroup>
                          <Label>Termos e Condições</Label>
                          <Input type="text" value={formData.offer?.termsConditions || ""}
                            onChange={(e) => setFormData({ ...formData, offer: { ...formData.offer!, termsConditions: e.target.value } })}
                            placeholder="Ex: Válido até 31/12/2026" />
                        </FormGroup>
                      </ConditionalSection>
                    )}

                    <ConditionalSection style={{ marginBottom: 14 }}>
                      <ConditionalTitle>
                        <span className="material-icons">touch_app</span>
                        Botão de Ação (CTA)
                      </ConditionalTitle>
                      <FormGrid>
                        <FormGroup>
                          <Label>Tipo de Ação</Label>
                          <Select value={formData.callToAction?.actionType || ""}
                            onChange={(e) => setFormData({ ...formData, callToAction: { ...formData.callToAction!, actionType: e.target.value } })}>
                            <option value="">Sem botão</option>
                            <option value="BOOK">Reservar</option>
                            <option value="ORDER">Pedir</option>
                            <option value="SHOP">Comprar</option>
                            <option value="LEARN_MORE">Saiba Mais</option>
                            <option value="SIGN_UP">Inscrever-se</option>
                            <option value="CALL">Ligar</option>
                            <option value="GET_OFFER">Ver Oferta</option>
                          </Select>
                        </FormGroup>
                        <FormGroup>
                          <Label>URL do Botão</Label>
                          <Input type="url" value={formData.callToAction?.url || ""}
                            onChange={(e) => setFormData({ ...formData, callToAction: { ...formData.callToAction!, url: e.target.value } })}
                            placeholder="https://..." />
                        </FormGroup>
                      </FormGrid>
                    </ConditionalSection>

                    <FormGroup className="full-width" style={{ marginBottom: 14 }}>
                      <Label>
                        URL da Imagem (opcional)
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <Input type="url" value={formData.media?.[0]?.sourceUrl || ""}
                        onChange={(e) => setFormData({ ...formData, media: e.target.value ? [{ mediaFormat: "PHOTO", sourceUrl: e.target.value }] : undefined })}
                        placeholder="https://exemplo.com/imagem.jpg" />
                    </FormGroup>

                    <FormActions>
                      <Btn type="button" $variant="ghost" onClick={closeEditForm}>
                        <span className="material-icons">close</span>
                        Cancelar
                      </Btn>
                      <Btn type="button" $variant="primary" onClick={() => handleSave(true)} disabled={saving || !formData.summary}>
                        <span className="material-icons">save</span>
                        {saving ? "Salvando..." : "Atualizar"}
                      </Btn>
                    </FormActions>
                  </AddPostForm>
                </PostCardBody>
              ) : (
                /* ---------- Visualização normal do card ---------- */
                <>
                  <PostCardBody>
                    {post.media?.[0]?.sourceUrl && (
                      <PostMediaPreview>
                        <img src={post.media[0].sourceUrl} alt="Mídia do post" />
                      </PostMediaPreview>
                    )}

                    <PostSummaryText>{post.summary}</PostSummaryText>

                    {post.event && (
                      <EventBanner>
                        <p className="event-title">{post.event.title}</p>
                        <div className="event-time">
                          <span className="material-icons">schedule</span>
                          {formatDate(post.event.startTime)}
                          {post.event.endTime && ` — ${formatDate(post.event.endTime)}`}
                        </div>
                      </EventBanner>
                    )}

                    {post.offer && (
                      <OfferBanner>
                        {post.offer.couponCode && <span className="coupon-code">{post.offer.couponCode}</span>}
                        {post.offer.termsConditions && <p className="offer-terms">{post.offer.termsConditions}</p>}
                      </OfferBanner>
                    )}

                    {post.callToAction?.url && (
                      <CTALink href={post.callToAction.url} target="_blank" rel="noopener noreferrer">
                        {getCTALabel(post.callToAction.actionType)}
                        <span className="material-icons">open_in_new</span>
                      </CTALink>
                    )}

                    <PostMetaRow>
                      {post.createTime && (
                        <span className="meta-item">
                          <span className="material-icons">calendar_today</span>
                          {formatDate(post.createTime)}
                        </span>
                      )}
                      {post.metrics?.viewsCount !== undefined && (
                        <span className="meta-item">
                          <span className="material-icons">visibility</span>
                          {post.metrics.viewsCount} visualizações
                        </span>
                      )}
                      {post.metrics?.clicksCount !== undefined && (
                        <span className="meta-item">
                          <span className="material-icons">touch_app</span>
                          {post.metrics.clicksCount} cliques
                        </span>
                      )}
                    </PostMetaRow>
                  </PostCardBody>

                  {!readOnly && (
                    <PostCardActions>
                      <ActionBtn type="button" onClick={() => openEditForm(post)}>
                        <span className="material-icons">edit</span>
                        Editar
                      </ActionBtn>
                      <ActionBtn type="button" $danger onClick={() => setDeleteConfirm(post.postId || "")}>
                        <span className="material-icons">delete</span>
                        Excluir
                      </ActionBtn>
                    </PostCardActions>
                  )}
                </>
              )}
            </PostCard>
          ))}
        </PostsList>
      )}

      {/* Modal de Confirmação de Delete */}
      {deleteConfirm && (
        <ModalBackdrop
          onClick={(e) =>
            e.target === e.currentTarget && setDeleteConfirm(null)
          }
        >
          <ModalBox>
            <ModalBoxHeader>
              <h4>Confirmar Exclusão</h4>
              <CloseBtn
                type="button"
                onClick={() => setDeleteConfirm(null)}
              >
                <span className="material-icons">close</span>
              </CloseBtn>
            </ModalBoxHeader>
            <ModalBoxBody>
              Tem certeza que deseja excluir esta postagem? Esta ação não pode
              ser desfeita.
            </ModalBoxBody>
            <ModalBoxFooter>
              <Btn
                type="button"
                $variant="ghost"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancelar
              </Btn>
              <Btn
                type="button"
                $variant="danger"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={saving}
              >
                <span className="material-icons">delete_forever</span>
                {saving ? "Excluindo..." : "Excluir"}
              </Btn>
            </ModalBoxFooter>
          </ModalBox>
        </ModalBackdrop>
      )}
    </Container>
  );
};

export default PostsTab;
